import { Box, IconButton, Tooltip } from "@mui/material";
import {
  Button,
  DatePicker,
  Grid,
  Input,
  Loader,
  Modal,
  PhoneInput,
  Switch,
  Typography,
} from "../../../components";
import { ContentCopy } from "@mui/icons-material";
import { Fragment, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { formatCPF, formatDate } from "../../../utils";
import { enqueueSnackbar } from "notistack";
import { useAxios } from "../../../hooks";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AuthContext from "../../../context/auth";
import { useFormik } from "formik";
import {
  isSmilesFamilyCompany,
  UpdateAccountValidationSchema,
} from "@next-pontos/validations";
import moment from "moment";
import { AxiosError } from "axios";

export type AccountModalBidAccount = {
  user_id?: number;
  is_membership_active: boolean;
  login: string;
  holder_name: string;
  holder_email: string;
  holder_cpf: string;
  holder_birthday: Date | string;
  holder_phone_number?: string;
  membership_number?: string | null;
};

export interface AccountModalBidsProps {
  accountModalVisibility: boolean;
  accountModalVisibilityControls: {
    setFalse: () => void;
  };
  loading: boolean;
  bid?: {
    id?: number;
    company?: string;
    account_id: string | number;
    account: AccountModalBidAccount;
  };
  onAccountSaved?: () => void;
}

export const AccountModalBids = ({
  accountModalVisibility,
  accountModalVisibilityControls,
  loading,
  bid,
  onAccountSaved,
}: AccountModalBidsProps) => {
  const { me } = useContext(AuthContext);
  const [passwordData, setPasswordData] = useState<null | { password: string }>(
    null
  );
  const [isEditing, setIsEditing] = useState(false);
  const [savedAccountPatch, setSavedAccountPatch] = useState<
    Partial<AccountModalBidAccount> | null
  >(null);

  const [password, fetchPassword] = useAxios("/account", {
    manual: true,
  });

  const [, updateAccount] = useAxios("/account", {
    manual: true,
  });

  const displayAccount = useMemo(() => {
    if (!bid?.account) return null;
    return { ...bid.account, ...savedAccountPatch };
  }, [bid?.account, savedAccountPatch]);

  const canEditAccount =
    !!displayAccount &&
    !!me &&
    (me.role === "ADMIN" ||
      (typeof displayAccount.user_id === "number" &&
        displayAccount.user_id === me.id));

  const handleRevealPassword = useCallback(async () => {
    const passwordResponse = await fetchPassword({
      url: `/account/${bid?.account_id}/password`,
      method: "GET",
    });

    setPasswordData(passwordResponse.data);
  }, [fetchPassword, bid?.account_id]);

  const initialFormValues = useMemo(() => {
    const a = displayAccount;
    if (!a) {
      return {
        company: bid?.company ?? "",
        holder_name: "",
        holder_email: "",
        holder_phone_number: "",
        holder_cpf: "",
        login: "",
        password: "",
        is_membership_active: false,
        holder_birthday: "" as Date | "",
        membership_number: "",
      };
    }
    return {
      company: bid?.company ?? "",
      holder_name: a.holder_name ?? "",
      holder_email: a.holder_email ?? "",
      holder_phone_number: a.holder_phone_number ?? "",
      holder_cpf: formatCPF(a.holder_cpf || ""),
      login: a.login ?? "",
      password: "",
      is_membership_active: !!a.is_membership_active,
      holder_birthday: a.holder_birthday
        ? new Date(a.holder_birthday)
        : ("" as Date | ""),
      membership_number: a.membership_number ?? "",
    };
  }, [displayAccount, bid?.company]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialFormValues,
    validationSchema: UpdateAccountValidationSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      const payload = {
        holder_name: values.holder_name,
        holder_email: values.holder_email,
        holder_phone_number: values.holder_phone_number,
        holder_cpf: values.holder_cpf,
        login: values.login,
        is_membership_active: values.is_membership_active,
        holder_birthday: values.holder_birthday,
        membership_number: values.membership_number,
        ...(values.password ? { password: values.password } : {}),
      };

      try {
        await updateAccount({
          url: `/account/${bid?.account_id}`,
          method: "PUT",
          data: payload,
        });
      } catch (err) {
        const msg =
          (err as AxiosError<{ message?: string }>)?.response?.data?.message ??
          "Não foi possível salvar a conta.";
        enqueueSnackbar(msg, { variant: "error" });
        return;
      }

      setSavedAccountPatch({
        holder_name: values.holder_name,
        holder_email: values.holder_email,
        holder_phone_number: values.holder_phone_number,
        holder_cpf: values.holder_cpf.replace(/\D/g, ""),
        login: values.login,
        is_membership_active: values.is_membership_active,
        holder_birthday: values.holder_birthday as Date,
        membership_number: values.membership_number,
      });
      setPasswordData(null);
      setIsEditing(false);
      enqueueSnackbar("Conta atualizada com sucesso.", { variant: "success" });
      onAccountSaved?.();
    },
  });

  useEffect(() => {
    if (!accountModalVisibility) {
      setPasswordData(null);
      setIsEditing(false);
      setSavedAccountPatch(null);
    }
  }, [accountModalVisibility]);

  const handleStartEdit = () => {
    formik.resetForm({ values: initialFormValues });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    formik.resetForm({ values: initialFormValues });
    setIsEditing(false);
  };

  const modalWidth = isEditing ? 720 : 600;

  return (
    <Modal
      open={accountModalVisibility}
      onClose={accountModalVisibilityControls.setFalse}
      title="Dados da Conta"
      bodyCustomCss={{ minWidth: modalWidth, maxWidth: "min(96vw, 800px)" }}
    >
      {loading || password.loading ? (
        <div style={{ textAlign: "center", width: modalWidth }}>
          <Loader />
        </div>
      ) : !displayAccount ? (
        <div style={{ textAlign: "center", width: modalWidth }}>
          <Typography>Conta não encontrada.</Typography>
        </div>
      ) : isEditing ? (
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2} width={modalWidth}>
            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end" gap={1}>
                <Button variant="outlined" type="button" onClick={handleCancelEdit}>
                  Cancelar
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} display="flex" alignItems="center">
              <Switch
                label="Possui clube ativo?"
                checked={formik.values.is_membership_active}
                onChange={() => {
                  formik.setFieldValue(
                    "is_membership_active",
                    !formik.values.is_membership_active,
                    false
                  );
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} />
            <Grid item xs={12} sm={6}>
              <Input
                label="Nome do Titular"
                name="holder_name"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.holder_name}
                helperText={formik.touched.holder_name && formik.errors.holder_name}
                error={!!formik.touched.holder_name && !!formik.errors.holder_name}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Input
                label="E-mail do Titular"
                name="holder_email"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.holder_email}
                helperText={formik.touched.holder_email && formik.errors.holder_email}
                error={!!formik.touched.holder_email && !!formik.errors.holder_email}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Data de Nascimento do Titular"
                value={
                  formik.values.holder_birthday
                    ? moment(formik.values.holder_birthday)
                    : null
                }
                onChange={(value) => {
                  formik.setFieldValue("holder_birthday", value?.toDate(), true);
                }}
                helperText={
                  formik.touched.holder_birthday && formik.errors.holder_birthday
                }
                error={
                  !!formik.touched.holder_birthday && !!formik.errors.holder_birthday
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Tooltip title="Número de fidelidade da companhia; se não houver, use o CPF.">
                <Input
                  label="Número de Fidelidade"
                  name="membership_number"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.membership_number}
                  helperText={
                    formik.touched.membership_number && formik.errors.membership_number
                  }
                  error={
                    !!formik.touched.membership_number &&
                    !!formik.errors.membership_number
                  }
                />
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Input
                label="CPF do Titular"
                name="holder_cpf"
                onChange={({ target }) => {
                  formik.setFieldValue("holder_cpf", formatCPF(target.value));
                }}
                onBlur={formik.handleBlur}
                value={formik.values.holder_cpf}
                helperText={formik.touched.holder_cpf && formik.errors.holder_cpf}
                error={!!formik.touched.holder_cpf && !!formik.errors.holder_cpf}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <PhoneInput
                label="Telefone do Titular"
                inputProps={{
                  name: "holder_phone_number",
                }}
                onChange={(value) => {
                  formik.setFieldValue("holder_phone_number", value, true);
                }}
                value={formik.values.holder_phone_number}
                onBlur={formik.handleBlur}
                error={
                  !!formik.touched.holder_phone_number &&
                  !!formik.errors.holder_phone_number
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Input
                label="Login"
                name="login"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.login}
                helperText={formik.touched.login && formik.errors.login}
                error={!!formik.touched.login && !!formik.errors.login}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Input
                label="Nova senha (opcional)"
                name="password"
                type="password"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.password}
                helperText={formik.touched.password && formik.errors.password}
                error={!!formik.touched.password && !!formik.errors.password}
                inputProps={
                  isSmilesFamilyCompany(formik.values.company)
                    ? { inputMode: "numeric", maxLength: 4 }
                    : undefined
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Button disabled={!formik.isValid || formik.isSubmitting} type="submit">
                Salvar
              </Button>
            </Grid>
          </Grid>
        </form>
      ) : (
        <Grid container spacing={2} width={modalWidth}>
          {canEditAccount && (
            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end">
                <Button variant="outlined" onClick={handleStartEdit}>
                  Editar
                </Button>
              </Box>
            </Grid>
          )}
          <Grid item xs={12}>
            <Grid container direction="row">
              <Grid item xs={6}>
                <Typography fontWeight="bold">Clube Ativo:</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography textAlign="end">
                  {displayAccount?.is_membership_active ? "Sim" : "Não"}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container direction="row">
              <Grid item xs={6}>
                <Typography fontWeight="bold">Login:</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography textAlign="end">
                  {displayAccount?.login}
                  <IconButton
                    size="small"
                    sx={{ ml: 1 }}
                    color="primary"
                    onClick={() => {
                      navigator?.clipboard?.writeText(displayAccount?.login || "");
                      enqueueSnackbar("Copiado!", { variant: "success" });
                    }}
                  >
                    <ContentCopy />
                  </IconButton>
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container direction="row">
              <Grid item xs={6}>
                <Typography fontWeight="bold">Senha:</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography textAlign="end">
                  {passwordData ? (
                    <Fragment>
                      {passwordData?.password}
                      <IconButton
                        size="small"
                        sx={{ ml: 1 }}
                        color="primary"
                        onClick={() => {
                          navigator?.clipboard?.writeText(passwordData?.password);
                          enqueueSnackbar("Copiado!", { variant: "success" });
                        }}
                      >
                        <ContentCopy />
                      </IconButton>
                    </Fragment>
                  ) : (
                    <div
                      style={{ cursor: "pointer" }}
                      onClick={handleRevealPassword}
                    >
                      <VisibilityIcon fontSize="inherit" />
                    </div>
                  )}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container direction="row">
              <Grid item xs={6}>
                <Typography fontWeight="bold">Nome do Titular:</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography textAlign="end">
                  {displayAccount?.holder_name}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container direction="row">
              <Grid item xs={6}>
                <Typography fontWeight="bold">E-mail do Titular:</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography textAlign="end">
                  {displayAccount?.holder_email}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container direction="row">
              <Grid item xs={6}>
                <Typography fontWeight="bold">Telefone do Titular:</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography textAlign="end">
                  {displayAccount?.holder_phone_number ?? "—"}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container direction="row">
              <Grid item xs={6}>
                <Typography fontWeight="bold">Número de Fidelidade:</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography textAlign="end">
                  {displayAccount?.membership_number ?? "—"}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container direction="row">
              <Grid item xs={6}>
                <Typography fontWeight="bold">CPF do Titular:</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography textAlign="end">
                  {formatCPF(displayAccount?.holder_cpf || "")}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container direction="row">
              <Grid item xs={6}>
                <Typography fontWeight="bold">Data de Nasc. do Titular:</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography textAlign="end">
                  {displayAccount?.holder_birthday
                    ? formatDate(new Date(displayAccount.holder_birthday))
                    : "—"}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Modal>
  );
};
